import { DocumentIcon, DocumentTextIcon, MagnifyingGlassIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import Modal from "components/common/Modal";
import { useLanguage } from "contexts/LanguageContext";
import { formatDocumentDate } from "utils/formatters";
import { Customer } from "types/customer";
import { useState, useEffect } from "react";
import { Quotation } from "types/quotation";
import { isQuotation } from "./DocumentGeneratorModal";

interface DocumentViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerWithDocuments: Customer | Quotation | null;
}

const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({ isOpen, onClose, customerWithDocuments }) => {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');

  // Modal megnyitásakor töröljük az állapotokat
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      console.log("DocumentViewerModal - dokumentumok:", customerWithDocuments?.documents);
    }
  }, [isOpen, customerWithDocuments]);

  // Dokumentumok kezelése
  let documents: any[] = [];
  if (customerWithDocuments && customerWithDocuments.documents) {
    documents = customerWithDocuments.documents;
  }

  let filteredDocuments = documents;
  if (query) {
    filteredDocuments = documents.filter(doc => 
      doc.name.toLowerCase().includes(query.toLowerCase())
    );
  }

  // Ellenőrizzük, hogy vannak-e dokumentumok
  const hasDocuments = documents.length > 0;
  
  // Címe előállítása
  const getModalTitle = () => {
    if (!customerWithDocuments) {
      return t('documents.documentModal.documentViewer') || 'Dokumentumok';
    }
    
    if (isQuotation(customerWithDocuments)) {
      return t('documents.documentModal.title', { name: customerWithDocuments.number || '' });
    }
    
    return t('documents.documentModal.title', {
      name: customerWithDocuments.type === 'organization'
        ? customerWithDocuments.companyName || ''
        : `${customerWithDocuments.firstName || ''} ${customerWithDocuments.lastName || ''}`.trim()
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      size="lg"
    >
      <div className="flex flex-col max-h-[80vh]">
        {!hasDocuments ? (
          <div className="py-8 px-4 flex flex-col items-center justify-center">
            <DocumentIcon className="h-10 w-10 text-gray-400 mb-3" />
            <p className="text-gray-600 text-base font-medium mb-1">
              {t('documents.documentModal.noDocuments')}
            </p>
            <p className="text-gray-500 text-sm text-center">
              {t('documents.documentModal.noDocumentsDescription') || 'No documents have been generated or uploaded yet for this item.'}
            </p>
          </div>
        ) : (
          <>
            {/* Keresési sáv */}
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <input
                  type="text"
                  className="w-full pl-9 pr-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('documents.documentModal.search') || "Dokumentum keresése..."}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              </div>
            </div>
            
            {/* Dokumentum lista */}
            <div className="overflow-y-auto p-3">
              {filteredDocuments.length === 0 ? (
                <div className="py-4 flex flex-col items-center justify-center">
                  <p className="text-gray-500 text-sm">
                    {query ? t('documents.documentModal.noResults') : t('documents.documentModal.selectDocument')}
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {filteredDocuments.map((doc, index) => (
                    <li key={index} className="py-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center min-w-0 pr-2">
                          <DocumentTextIcon className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0" />
                          <div className="truncate">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {doc.originalName || doc.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatDocumentDate(doc.uploadedAt)}
                            </p>
                          </div>
                        </div>
                        <a
                          href={doc.url}
                          download={doc.originalName || doc.name}
                          className="p-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors flex-shrink-0"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                        </a>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default DocumentViewerModal;